import Application from '@ember/application';
import type { FullName } from '@ember/owner';

// Legacy jQuery vendor stack (jQuery + jQuery UI + plugins). Formerly loaded via
// `app.import()` into /@embroider/virtual/vendor.js; now an ESM bootstrap. Imported
// first so the global jQuery + plugins exist before any component/module uses `$`.
import 'webapp/vendor/jquery-stack';

import setupInspector from '@embroider/legacy-inspector-support/ember-source-4.12';
import { importSync, isDevelopingApp, macroCondition } from '@embroider/macros';

// Wire WarpDrive's reactivity to Ember's tracking system. Formerly auto-installed
// by the (now-deprecated) `@ember-data/tracking` package; the store no longer sets
// this up itself, so we install it explicitly. Must run before any store is
// instantiated — see the `DEPRECATE_TRACKING_PACKAGE: false` note in babel.config.mjs.
import '@warp-drive/ember/install';

import compatModules from '@embroider/virtual/compat-modules';
import loadInitializers from 'ember-load-initializers';
import Resolver from 'ember-resolver';

import config from 'webapp/config/environment';
// Legacy LESS pipeline. Imported here (rather than compiled by ember-cli-less
// into /@embroider/virtual/app.css) so Vite's built-in LESS compiler processes
// it directly. Must load BEFORE tailwind.css so unlayered `tw:` utilities keep
// winning over legacy LESS in the cascade.
import 'webapp/styles/app.less';
// Tailwind design tokens for the light DOM. Imported here (rather than linked
// from index.html) so Vite processes it and both the app and the test build
// pick it up from the single entry point. Loads after the legacy LESS above,
// which is what lets unlayered `tw:` utilities win over legacy LESS.
import 'webapp/styles/tailwind.css';

// Dev-only API mock data (window.dbx_mock). Formerly app.import'd into
// /@embroider/virtual/vendor.js; referenced by commented-out helpers in
// app/data-loader. Gated by isDevelopingApp so it never ships to production.
// `macroCondition` must be the direct predicate of the `if` (it can't be assigned to a
// variable first) so the macros build plugin can statically strip this in production.
if (macroCondition(isDevelopingApp())) {
	importSync('/vendor/mocks/mock.js');
}

// polyfill for randomUUID for older TVs, loaded before Ember Data
if (typeof crypto.randomUUID !== 'function') {
	crypto.randomUUID = function randomUUID() {
		// Polyfill implementation (RFC4122 version 4 compliant)
		// See: https://developer.mozilla.org/en-US/docs/Web/API/Crypto/randomUUID#polyfill
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
			const r = (crypto.getRandomValues(new Uint8Array(1))[0]! & 0xf) >> 0;
			const v = c === 'x' ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		}) as ReturnType<Crypto['randomUUID']>;
	};
}

let AppResolver = Resolver.withModules(compatModules);

// ember-vite-hmr (dev server only) looks routes up as `<name>--hot-version--<n>`, so a route's own
// controller and `controllerFor('<name>')` would be separate instances. Controller keys drop the suffix
// to share one; route keys keep it, since that is how HMR gets fresh routes. Stripped from production
// builds like the mock import above.
if (macroCondition(isDevelopingApp())) {
	const HOT_VERSION_SUFFIX = /--hot-version--.*$/;

	AppResolver = class extends AppResolver {
		override normalize = (fullName: FullName): FullName => {
			const normalized = Resolver.prototype.normalize.call(this, fullName);

			return normalized.startsWith('controller:')
				? (normalized.replace(HOT_VERSION_SUFFIX, '') as FullName)
				: normalized;
		};
	};
}

export default class App extends Application {
	modulePrefix = config.modulePrefix;
	podModulePrefix = config.podModulePrefix;
	override Resolver = AppResolver;
	inspector = setupInspector(this);
}

loadInitializers(App, config.modulePrefix, compatModules);
